import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("teams");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [circles, setCircles] = useState([]);
  const [form, setForm] = useState({ team_code: "", circle_name: user?.circle_name || "", technician_id: "", helper_id: "", supervisor_id: "" });
  const [podForm, setPodForm] = useState({ pod_code: "", circle_name: user?.circle_name || "", supervisor_id: "", max_teams: 5 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamRes, podRes, empRes, circleRes] = await Promise.all([
        api.get("/teams"), api.get("/pods"),
        api.get("/employees", { params: { limit: 100 } }),
        api.get("/hierarchy/circles")
      ]);
      setTeams(teamRes.data.items);
      setPods(podRes.data.items);
      setEmployees(empRes.data.items);
      setCircles(circleRes.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/teams", form);
      toast.success("Team created");
      setShowCreate(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const handleCreatePod = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/pods", podForm);
      toast.success("Pod created");
      setShowCreate(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const techEmployees = employees.filter(e => e.role === "technician");
  const supervisors = employees.filter(e => e.role === "supervisor");

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="teams-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Teams & Pods</h1>
          <p className="text-slate-500 text-sm">{teams.length} teams · {pods.length} pods</p>
        </div>
        {["admin", "ci"].includes(user?.role) && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600" data-testid="create-team-btn">
            <Plus size={16} />Create {activeTab === "teams" ? "Team" : "Pod"}
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 w-fit">
        {[{ id: "teams", label: `Teams (${teams.length})` }, { id: "pods", label: `Pods (${pods.length})` }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-sm text-xs font-semibold ${activeTab === tab.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`team-tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "teams" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead><tr><th>Team Code</th><th>Circle</th><th>Technician</th><th>Helper</th><th>Supervisor</th><th>Capacity</th><th>Status</th></tr></thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(7)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>)
                : teams.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-400"><Users size={32} className="mx-auto mb-2" />No teams yet</td></tr>
                ) : teams.map(t => (
                  <tr key={t.id}>
                    <td><span className="font-mono text-xs font-bold">{t.team_code}</span></td>
                    <td className="text-xs text-slate-600">{t.circle_name}</td>
                    <td className="text-xs">{t.technician_id?.slice(-8) || "—"}</td>
                    <td className="text-xs">{t.helper_id?.slice(-8) || "—"}</td>
                    <td className="text-xs">{t.supervisor_id?.slice(-8) || "—"}</td>
                    <td className="text-xs font-semibold">{t.avg_daily_capacity}/day</td>
                    <td><span className={`text-[10px] font-bold uppercase ${t.status === "active" ? "text-green-600" : "text-red-500"}`}>{t.status}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "pods" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead><tr><th>Pod Code</th><th>Circle</th><th>Supervisor</th><th>Max Teams</th><th>Status</th></tr></thead>
            <tbody>
              {pods.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400"><Building2 size={32} className="mx-auto mb-2" />No pods yet</td></tr>
              ) : pods.map(p => (
                <tr key={p.id}>
                  <td><span className="font-mono text-xs font-bold">{p.pod_code}</span></td>
                  <td className="text-xs text-slate-600">{p.circle_name}</td>
                  <td className="text-xs">{p.supervisor_id?.slice(-8) || "—"}</td>
                  <td className="text-xs font-semibold">{p.max_teams}</td>
                  <td><span className={`text-[10px] font-bold uppercase ${p.status === "active" ? "text-green-600" : "text-red-500"}`}>{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && activeTab === "teams" && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "Manrope" }}>Create Team</h3>
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="field-label">Team Code *</label><input className="field-input" required value={form.team_code} onChange={e => setForm({ ...form, team_code: e.target.value })} data-testid="team-code-input" /></div>
                <div>
                  <label className="field-label">Circle *</label>
                  <select className="field-select" required value={form.circle_name} onChange={e => setForm({ ...form, circle_name: e.target.value })}>
                    <option value="">Select circle</option>
                    {circles.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="field-label">Technician</label>
                <select className="field-select" value={form.technician_id} onChange={e => setForm({ ...form, technician_id: e.target.value })}>
                  <option value="">Select technician</option>
                  {techEmployees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Supervisor</label>
                <select className="field-select" value={form.supervisor_id} onChange={e => setForm({ ...form, supervisor_id: e.target.value })}>
                  <option value="">Select supervisor</option>
                  {supervisors.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60">
                  {creating ? "Creating..." : "Create Team"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
